import { analyticsService } from '@meiuca/analytics';
import { ANALYTICS_EVENTS, SCREEN_NAMES } from '@/constants/analyticsEvents'
import React, { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Phone, ArrowLeft } from 'lucide-react'
import { z } from 'zod'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Body, ButtonVariant, Input, INPUT_MASKS } from '@meiuca/ui-components'
import { toastManager } from '@/lib/toastManager'
import { passwordService } from '@/services/passwordService'

const forgotSchema = z.object({
  phone: z.string()
    .min(1, 'Telefone é obrigatório')
    .transform((val) => val.replace(/\D/g, ''))
    .refine((val) => val.length >= 10, 'Telefone deve ter pelo menos 10 dígitos')
    .refine((val) => val.length <= 11, 'Telefone deve ter no máximo 11 dígitos')
    .refine((val) => /^[1-9]{2}[2-9][0-9]{7,8}$/.test(val), 'Telefone inválido'),
})

type ForgotFormData = z.infer<typeof forgotSchema>

export default function ForgotPasswordPage() {
  useEffect(() => {
    analyticsService.trackScreenView(SCREEN_NAMES.FORGOT_PASSWORD);
  }, []);
  const navigate = useNavigate()

  const particles = useMemo(() => (
    Array.from({ length: 6 }).map((_, i) => ({
      id: i,
      left: `${(i * 15 + 10)}%`,
      top: `${(i * 10 + 20)}%`,
      duration: i * 0.5 + 3,
      delay: i * 0.3,
    }))
  ), [])

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotFormData>({
    resolver: zodResolver(forgotSchema),
  })

  const onSubmit = async (data: ForgotFormData) => {
    try {
      await passwordService.requestPasswordReset(data.phone)
      analyticsService.track(ANALYTICS_EVENTS.FORGOT_PASSWORD_SUBMITTED, { contact_method: 'phone' })
      toastManager.success('Enviamos um link de redefinição para seu telefone.')
      setTimeout(() => navigate('/login'), 800)
    } catch (error) {
      analyticsService.track(ANALYTICS_EVENTS.FORGOT_PASSWORD_ERROR, {
        error_message: error instanceof Error ? error.message : 'Unknown error'
      })
      const message = error instanceof Error ? error.message : 'Falha ao solicitar redefinição.'
      toastManager.error(message)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brand-primary/5 via-background-primary to-brand-secondary/5">
      {/* Background Animation */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute -top-40 -right-40 w-80 h-80 bg-brand-primary/10 rounded-full"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />

        <motion.div
          className="absolute -bottom-40 -left-40 w-96 h-96 bg-brand-secondary/10 rounded-full"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 2,
          }}
        />

        {/* Floating particles */}
        {particles.map((particle) => (
          <motion.div
            key={particle.id}
            className="absolute w-2 h-2 bg-brand-primary/20 rounded-full"
            style={{
              left: particle.left,
              top: particle.top,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.2, 0.8, 0.2],
            }}
            transition={{
              duration: particle.duration,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: particle.delay,
            }}
          />
        ))}
      </div>

      {/* Back Link */}
      <motion.button
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        onClick={() => navigate('/login')}
        className="absolute top-4 left-4 z-20 flex items-center space-x-2 text-text-secondary hover:text-text-primary transition-colors"
      >
        <ArrowLeft className="w-5 h-5" />
        <span>Voltar</span>
      </motion.button>

      {/* Main Content */}
      <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
        <div className="w-full max-w-md">
          {/* Background Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
            className="bg-background-white rounded-2xl shadow-2xl p-8 backdrop-blur-sm border border-white/20"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="w-full max-w-md mx-auto"
            >
              <div className="text-center mb-8">
                <motion.div
                  className="w-16 h-16 bg-gradient-to-br from-brand-primary to-brand-secondary rounded-2xl mx-auto mb-6 flex items-center justify-center"
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <span className="text-2xl font-bold text-white">M</span>
                </motion.div>

                <Body className="text-text-secondary">
                  Informe seu telefone para receber o link de redefinição
                </Body>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" noValidate>
                <Input
                  {...register('phone')}
                  type="tel"
                  label="Telefone"
                  placeholder="(11) 99999-9999"
                  mask={INPUT_MASKS.PHONE_BR}
                  unmask={true}
                  leftIcon={Phone}
                  error={!!errors.phone}
                  errorMessage={errors.phone?.message}
                  fullWidth
                  className="w-full"
                  autoComplete="tel"
                />

                <Button
                  variant={ButtonVariant.PRIMARY}
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full"
                >
                  {isSubmitting ? 'Enviando...' : 'Enviar link de redefinição'}
                </Button>
              </form>

              <div className="mt-8 text-center">
                <Body className="text-text-secondary">
                  Lembrou sua senha?{' '}
                  <button
                    onClick={() => navigate('/login')}
                    className="text-brand-primary hover:text-brand-secondary font-medium transition-colors"
                    type="button"
                  >
                    Voltar para login
                  </button>
                </Body>
              </div>
            </motion.div>
          </motion.div>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-center mt-8"
          >
            <p className="text-sm text-text-tertiary">
              © 2025 Meiuca App. Todos os direitos reservados.
            </p>
          </motion.div>
        </div>
      </div>
    </div>
  )
}
